import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const { FiZap, FiSearch, FiCalendar, FiFilter, FiActivity, FiTag } = FiIcons;

const CustomEventsDashboard = () => {
  const { analyticsData } = useAnalytics();
  const [searchTerm, setSearchTerm] = useState('');

  const eventStats = useMemo(() => {
    const stats = {};
    analyticsData.events.forEach(event => {
      if (!stats[event.eventName]) {
        stats[event.eventName] = { name: event.eventName, count: 0, lastOccurred: 0, uniqueUsers: new Set() };
      }
      stats[event.eventName].count++;
      stats[event.eventName].uniqueUsers.add(event.visitorId);
      if (event.timestamp > stats[event.eventName].lastOccurred) {
        stats[event.eventName].lastOccurred = event.timestamp;
      }
    });

    return Object.values(stats)
      .map(s => ({ ...s, uniqueUsers: s.uniqueUsers.size }))
      .sort((a, b) => b.count - a.count);
  }, [analyticsData.events]);

  const filteredEvents = eventStats.filter(e => 
    e.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const COLORS = ['#3b82f6', '#10b981', '#6366f1', '#f59e0b', '#ef4444'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiZap} className="text-indigo-600" />
            Custom Event Tracking
          </h2>
          <p className="text-sm text-gray-500">Monitor triggers, interactions, and custom behavioral signals</p>
        </div>
        <div className="relative w-full md:w-64">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search events..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Event Distribution Chart */}
        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
            <SafeIcon icon={FiActivity} className="text-blue-500" />
            Event Distribution
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={eventStats.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip 
                  cursor={{ fill: '#f9fafb' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {eventStats.slice(0, 5).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Summary */}
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 p-6 rounded-xl text-white shadow-lg shadow-indigo-100 flex flex-col justify-between">
          <div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4 backdrop-blur-sm">
              <SafeIcon icon={FiTag} className="text-white text-xl" />
            </div>
            <h3 className="text-lg font-bold">Event Intelligence</h3>
            <p className="text-indigo-100 text-sm mt-2 leading-relaxed">
              You are currently tracking {eventStats.length} unique event types across your platform.
            </p>
          </div>
          <div className="mt-8 space-y-4">
            <div className="flex justify-between items-center text-sm border-b border-white/10 pb-2">
              <span className="text-indigo-200">Total Events Captured</span>
              <span className="font-bold">{analyticsData.events.length}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-indigo-200">Most Active Event</span>
              <span className="font-bold">{eventStats[0]?.name || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-500 tracking-wider">
            <tr>
              <th className="px-6 py-4">Event Name</th>
              <th className="px-6 py-4 text-center">Total Count</th>
              <th className="px-6 py-4 text-center">Unique Users</th>
              <th className="px-6 py-4">Last Captured</th>
              <th className="px-6 py-4">Trend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 italic-text-sm">
            {filteredEvents.map((event, idx) => (
              <motion.tr 
                key={event.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="hover:bg-indigo-50/30 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                      <SafeIcon icon={FiZap} className="text-xs" />
                    </div>
                    <span className="font-bold text-gray-900">{event.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center font-mono font-bold text-gray-700">{event.count}</td>
                <td className="px-6 py-4 text-center text-gray-600">{event.uniqueUsers}</td>
                <td className="px-6 py-4 text-gray-500 text-xs">
                  {new Date(event.lastOccurred).toLocaleString()}
                </td>
                <td className="px-6 py-4">
                  <div className={`h-1.5 rounded-full bg-gray-100 overflow-hidden w-24`}>
                    <div 
                      className="h-full bg-indigo-500" 
                      style={{ width: `${(event.count / eventStats[0].count) * 100}%` }}
                    />
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomEventsDashboard;